import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ProductItem, ProductRedirectionType } from 'types';

type CachedProductPages = {
  [id: string]: {
    data: ProductItem[];
    pageCount: number;
  };
};

export type ProductsStore = {
  isLoading: boolean;
  errorMsg: string;
  isCartBasketOpen: boolean;
  cachedProductListById: CachedProductPages;
  parentPageType: ProductRedirectionType | null;
};

const initialState: ProductsStore = {
  isLoading: false,
  errorMsg: '',
  isCartBasketOpen: false,
  cachedProductListById: {},
  parentPageType: null
};

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    toggleCartBasket: (state) => {
      state.isCartBasketOpen = !state.isCartBasketOpen;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.errorMsg = action.payload;
    },
    savePageDataById: (
      state,
      action: PayloadAction<{
        id: string;
        page: number;
        data: ProductItem[];
      }>
    ) => {
      const { id, page, data } = action.payload;

      if (!state.cachedProductListById[id]) {
        state.cachedProductListById[id] = {
          data: [],
          pageCount: 0
        };
      }

      const entry = state.cachedProductListById[id];
      const existingIds = new Set(entry.data.map((item) => item.id));
      const newItems = data.filter((item) => !existingIds.has(item.id));

      entry.data = [...entry.data, ...newItems];
      entry.pageCount = page;
    },
    setParentPageType: (
      state,
      action: PayloadAction<ProductRedirectionType>
    ) => {
      state.parentPageType = action.payload;
    },
    updateProductInStockNotify: (
      state,
      action: PayloadAction<{
        productId: number;
        notifyValue: 0 | 1;
      }>
    ) => {
      const { productId, notifyValue } = action.payload;

      Object.keys(state.cachedProductListById).forEach((id) => {
        const entry = state.cachedProductListById[id];
        const productIndex = entry.data.findIndex(
          (product) => product.id === productId
        );
        if (productIndex !== -1) {
          entry.data[productIndex] = {
            ...entry.data[productIndex],
            in_stock_notify: notifyValue
          };
        }
      });
    }
  }
});

export const {
  setIsLoading,
  setError,
  toggleCartBasket,
  savePageDataById,
  setParentPageType,
  updateProductInStockNotify
} = productsSlice.actions;

export default productsSlice.reducer;
